// Role utility functions to handle role name normalization
// Backend uses proper case: 'Admin', 'Diretor Comercial', etc.
// Frontend checks for lowercase: 'admin', 'diretor_comercial', etc.

export const normalizeRoleForFrontend = (role: string): string => {
  // Convert backend role names to frontend format
  const roleMap: { [key: string]: string } = {
    'Admin': 'admin',
    'Diretor Comercial': 'diretor_comercial',
    'Team Leader': 'team_leader',
    'team_leader': 'team_leader', // Handle actual database value
    'Gestor de Equipa': 'gestor_equipa',
    'Gestor de Equipa Júnior': 'gestor_equipa_junior',
    'Gestor Comercial': 'gestor_comercial'
  };
  
  return roleMap[role] || role.toLowerCase();
};

export const normalizeRoleForBackend = (role: string): string => {
  // Convert frontend role names to backend format
  const roleMap: { [key: string]: string } = {
    'admin': 'Admin',
    'diretor_comercial': 'Diretor Comercial',
    'team_leader': 'team_leader', // Map to actual database value
    'gestor_equipa': 'Gestor de Equipa',
    'gestor_equipa_junior': 'Gestor de Equipa Júnior',
    'gestor_comercial': 'Gestor Comercial'
  };
  
  return roleMap[role] || role;
};

// Role checking functions
export const isAdmin = (role: string): boolean => {
  return normalizeRoleForFrontend(role) === 'admin';
};

// Pages accessible to all authenticated users (non-admins only see these)
export const STANDARD_USER_ALLOWED_PATHS = ['/', '/client-workspace'] as const;

export const canAccessAppPage = (role: string, path: string): boolean => {
  if (isAdmin(role)) {
    return true;
  }
  const normalizedPath = path.split('?')[0];
  return (STANDARD_USER_ALLOWED_PATHS as readonly string[]).includes(normalizedPath);
};

export const isAdminOrDC = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial';
};

export const isAdminOrDCOrTeamLeader = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial' || normalizedRole === 'team_leader';
};

export const isGestorEquipaOrAbove = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  // Gestor de Equipa (index 2) and above: gestor_equipa, team_leader, diretor_comercial, admin
  return normalizedRole === 'gestor_equipa' || 
         normalizedRole === 'team_leader' || 
         normalizedRole === 'diretor_comercial' || 
         normalizedRole === 'admin';
};

export const canManageUsers = (role: string): boolean => {
  return isAdmin(role);
};

export const canManageTeams = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial' || normalizedRole === 'team_leader';
};

export const canAccessTeamsScreen = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial';
};

export const canSwitchBusinessArea = (role: string): boolean => {
  return isAdminOrDC(role);
};

export const getRoleLabel = (role: string): string => {
  const roleLabels: { [key: string]: string } = {
    'admin': 'Administrador',
    'diretor_comercial': 'Diretor Comercial',
    'team_leader': 'Team Leader',
    'gestor_equipa': 'Gestor de Equipa',
    'gestor_equipa_junior': 'Gestor de Equipa Júnior',
    'gestor_comercial': 'Gestor Comercial'
  };
  
  const normalizedRole = normalizeRoleForFrontend(role);
  return roleLabels[normalizedRole] || role;
};

export const getRoleColor = (role: string): string => {
  const roleColors: { [key: string]: string } = {
    'admin': 'red',
    'diretor_comercial': 'purple',
    'team_leader': 'blue',
    'gestor_equipa': 'green',
    'gestor_equipa_junior': 'orange',
    'gestor_comercial': 'cyan'
  };
  
  const normalizedRole = normalizeRoleForFrontend(role);
  return roleColors[normalizedRole] || 'default';
};

export const getBusinessAreaLabel = (area: string): string => {
  const areaLabels: { [key: string]: string } = {
    'telecommunications': 'Telecomunicações',
    'energy_solutions': 'Energia',
    'telecom': 'Telecomunicações', // Fallback for old values
    'energy': 'Energia' // Fallback for old values
  };
  
  return areaLabels[area] || area;
};

export const isTeamLeader = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'team_leader';
};

export const canAccessSimuladores = (userRole: string, userBusinessArea: string, currentBusinessArea: string): boolean => {
  // Users in energy business area can always access
  if (userBusinessArea === 'energy_solutions') {
    return true;
  }
  
  // Admins and DCs can access if they've switched to energy side
  if (canSwitchBusinessArea(userRole) && currentBusinessArea === 'energy_solutions') {
    return true;
  }
  
  return false;
};

export const canCreateNews = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || 
         normalizedRole === 'diretor_comercial' || 
         normalizedRole === 'team_leader' || 
         normalizedRole === 'gestor_equipa';
};

export const canCreateCategories = (role: string): boolean => {
  return isAdminOrDC(role);
};

export const canCreateFolders = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || 
         normalizedRole === 'diretor_comercial' || 
         normalizedRole === 'team_leader' || 
         normalizedRole === 'gestor_equipa';
}; 